import React, { useState } from 'react';

/**
 * RiskDetailsViewer Component
 * Provides comprehensive drill-down views for different risk types
 * Shows WHERE risks exist and HOW to investigate them
 */
const RiskDetailsViewer = ({ risk, prData }) => {
  const [expandedSection, setExpandedSection] = useState(null);

  // Parse risk text to identify type
  const parseRiskType = (riskText) => {
    if (riskText.includes('unresolved comment')) return 'unresolved-comments';
    if (riskText.includes('review comment')) return 'review-comments';
    if (riskText.includes('Large PR') || riskText.includes('lines changed')) return 'large-pr';
    if (riskText.includes('Merge Conflict')) return 'merge-conflicts';
    if (riskText.includes('Review Velocity')) return 'review-velocity';
    if (riskText.includes('Code Churn')) return 'code-churn';
    return 'unknown';
  };

  const riskType = parseRiskType(risk);

  // Extract numbers from risk text
  const extractNumber = (text) => {
    const match = text.match(/(\d+)/);
    return match ? parseInt(match[1]) : 0;
  };

  const renderUnresolvedComments = () => {
    const count = extractNumber(risk);
    return (
      <div style={{ padding: '15px', backgroundColor: '#fff3cd', borderRadius: '6px', marginTop: '12px' }}>
        <h5 style={{ margin: '0 0 12px 0', color: '#856404', fontSize: '14px', fontWeight: 'bold' }}>
          📌 Unresolved Comments Breakdown
        </h5>
        
        {/* Summary */}
        <div style={{ 
          padding: '12px', 
          backgroundColor: '#ffffff', 
          borderRadius: '4px', 
          marginBottom: '12px',
          border: '1px solid #ffc107'
        }}>
          <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#333' }}>
            <strong>Total Unresolved:</strong> {count} comment{count !== 1 ? 's' : ''}
          </p>
          <p style={{ margin: '0', fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
            These comments were marked as "needs resolution" but PR was merged anyway
          </p>
        </div>

        {/* Investigation Steps */}
        <div style={{ marginTop: '12px' }}>
          <p style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: 'bold', color: '#856404' }}>
            🔍 How to investigate:
          </p>
          <ol style={{ margin: '8px 0', paddingLeft: '20px', fontSize: '12px', color: '#555', lineHeight: '1.8' }}>
            <li>Visit the PR on GitHub</li>
            <li>Click "Conversation" tab</li>
            <li>Filter by "Outdated" or "Resolved" to see merged unresolved comments</li>
            <li>Check comment threads for:
              <ul style={{ margin: '4px 0 0 0', fontSize: '11px' }}>
                <li>Questions about implementation</li>
                <li>Security or performance concerns</li>
                <li>Requests for documentation</li>
              </ul>
            </li>
          </ol>
        </div>

        {/* Risk Assessment */}
        <div style={{ 
          marginTop: '12px', 
          padding: '10px', 
          backgroundColor: '#fff3cd', 
          borderRadius: '4px',
          borderLeft: '3px solid #ffc107'
        }}>
          <p style={{ margin: '0', fontSize: '12px', color: '#856404' }}>
            <strong>⚠️ Why this matters:</strong> Unresolved comments may indicate rushed reviews or incomplete feedback implementation. Could indicate quality or consistency issues.
          </p>
        </div>

        {/* Recommended Actions */}
        <div style={{ marginTop: '12px' }}>
          <p style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: 'bold', color: '#856404' }}>
            ✅ Recommended actions:
          </p>
          <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '12px', color: '#555', lineHeight: '1.8' }}>
            <li>Review the comment threads to understand what was not resolved</li>
            <li>Create follow-up issues for unaddressed concerns</li>
            <li>In future: Require all comments to be resolved before merge</li>
          </ul>
        </div>
      </div>
    );
  };

  const renderReviewComments = () => {
    const count = extractNumber(risk);
    return (
      <div style={{ padding: '15px', backgroundColor: '#e7f3ff', borderRadius: '6px', marginTop: '12px' }}>
        <h5 style={{ margin: '0 0 12px 0', color: '#004085', fontSize: '14px', fontWeight: 'bold' }}>
          👥 Review Comments Breakdown
        </h5>

        {/* Summary */}
        <div style={{ 
          padding: '12px', 
          backgroundColor: '#ffffff', 
          borderRadius: '4px', 
          marginBottom: '12px',
          border: '1px solid #b3d9ff'
        }}>
          <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#333' }}>
            <strong>Total Review Comments:</strong> {count} comment{count !== 1 ? 's' : ''}
          </p>
          <p style={{ margin: '0', fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
            This includes all feedback: suggestions, questions, and approvals
          </p>
        </div>

        {/* Breakdown Expected */}
        <div style={{ marginTop: '12px' }}>
          <p style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: 'bold', color: '#004085' }}>
            📊 Expected breakdown:
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px' }}>
            <div style={{ padding: '8px', backgroundColor: '#e7f3ff', borderRadius: '4px' }}>
              <strong>🔴 Requested Changes:</strong> How many reviewers requested changes?
            </div>
            <div style={{ padding: '8px', backgroundColor: '#e7f3ff', borderRadius: '4px' }}>
              <strong>💬 Comments:</strong> General feedback and discussions
            </div>
            <div style={{ padding: '8px', backgroundColor: '#e7f3ff', borderRadius: '4px' }}>
              <strong>✅ Approvals:</strong> How many approvals received?
            </div>
            <div style={{ padding: '8px', backgroundColor: '#e7f3ff', borderRadius: '4px' }}>
              <strong>⏱️ Time:</strong> How long between request and merge?
            </div>
          </div>
        </div>

        {/* Investigation Steps */}
        <div style={{ marginTop: '12px' }}>
          <p style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: 'bold', color: '#004085' }}>
            🔍 How to investigate:
          </p>
          <ol style={{ margin: '8px 0', paddingLeft: '20px', fontSize: '12px', color: '#555', lineHeight: '1.8' }}>
            <li>Visit PR → "Conversation" tab</li>
            <li>Look for reviewers and their status:
              <ul style={{ margin: '4px 0 0 0', fontSize: '11px' }}>
                <li>Green checkmark = Approved</li>
                <li>Yellow dot = Changes Requested</li>
                <li>Comment count per reviewer</li>
              </ul>
            </li>
            <li>Check "Files changed" tab to see comments on specific lines</li>
            <li>Review timeline: When was first review? When merged?</li>
          </ol>
        </div>

        {/* Assessment */}
        <div style={{ 
          marginTop: '12px', 
          padding: '10px', 
          backgroundColor: '#d1ecf1', 
          borderRadius: '4px',
          borderLeft: '3px solid #0c5460'
        }}>
          <p style={{ margin: '0', fontSize: '12px', color: '#0c5460' }}>
            <strong>📈 Analysis:</strong> High comment count could mean either thorough review (good) or contentious PR (concerning). Look at the tone and outcomes.
          </p>
        </div>

        {/* Recommended Actions */}
        <div style={{ marginTop: '12px' }}>
          <p style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: 'bold', color: '#004085' }}>
            ✅ Recommended actions:
          </p>
          <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '12px', color: '#555', lineHeight: '1.8' }}>
            <li>If high comments with all approved: Excellent peer review</li>
            <li>If high comments with changes requested: Investigate why merged quickly</li>
            <li>If low comments: May need more thorough code review process</li>
          </ul>
        </div>
      </div>
    );
  };

  const renderLargePR = () => {
    const match = risk.match(/\+(\d+)\/-(\d+)/);
    const added = match ? parseInt(match[1]) : 0;
    const removed = match ? parseInt(match[2]) : 0;
    const total = added + removed;
    const ratio = added > 0 ? ((removed / added) * 100).toFixed(1) : 0;

    return (
      <div style={{ padding: '15px', backgroundColor: '#fff3e0', borderRadius: '6px', marginTop: '12px' }}>
        <h5 style={{ margin: '0 0 12px 0', color: '#7a4419', fontSize: '14px', fontWeight: 'bold' }}>
          📈 Large PR Breakdown
        </h5>

        {/* Summary Stats */}
        <div style={{ 
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: '10px',
          marginBottom: '12px'
        }}>
          <div style={{ 
            padding: '10px', 
            backgroundColor: '#d7f5dd', 
            borderRadius: '4px',
            textAlign: 'center',
            borderLeft: '3px solid #28a745'
          }}>
            <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#155724' }}>
              +{added}
            </div>
            <div style={{ fontSize: '11px', color: '#155724' }}>Lines Added</div>
          </div>
          <div style={{ 
            padding: '10px', 
            backgroundColor: '#f5d7d7', 
            borderRadius: '4px',
            textAlign: 'center',
            borderLeft: '3px solid #dc3545'
          }}>
            <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#721c24' }}>
              -{removed}
            </div>
            <div style={{ fontSize: '11px', color: '#721c24' }}>Lines Removed</div>
          </div>
          <div style={{ 
            padding: '10px', 
            backgroundColor: '#fff3e0', 
            borderRadius: '4px',
            textAlign: 'center',
            borderLeft: '3px solid #ffc107'
          }}>
            <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#7a4419' }}>
              {total}
            </div>
            <div style={{ fontSize: '11px', color: '#7a4419' }}>Total Changed</div>
          </div>
        </div>

        {/* Change Distribution */}
        <div style={{ 
          padding: '12px', 
          backgroundColor: '#ffffff', 
          borderRadius: '4px', 
          marginBottom: '12px',
          border: '1px solid #ffc107'
        }}>
          <p style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: 'bold', color: '#7a4419' }}>
            📊 Change Analysis:
          </p>
          <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#555' }}>
            <strong>Deletion Ratio:</strong> {ratio}% (lines removed per line added)
          </p>
          <p style={{ margin: '0', fontSize: '11px', color: '#999', fontStyle: 'italic' }}>
            Lower ratio = More net additions (refactoring or new features). Higher ratio = More cleanup/removal.
          </p>
        </div>

        {/* Investigation Steps */}
        <div style={{ marginTop: '12px' }}>
          <p style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: 'bold', color: '#7a4419' }}>
            🔍 How to investigate:
          </p>
          <ol style={{ margin: '8px 0', paddingLeft: '20px', fontSize: '12px', color: '#555', lineHeight: '1.8' }}>
            <li>Visit PR → "Files changed" tab</li>
            <li>Identify top 5 files with most changes:
              <ul style={{ margin: '4px 0 0 0', fontSize: '11px' }}>
                <li>Are they all in same module?</li>
                <li>Multiple independent changes?</li>
                <li>Refactoring vs new features?</li>
              </ul>
            </li>
            <li>Check if changes are focused or scattered across codebase</li>
            <li>Review commit messages to understand intent</li>
          </ol>
        </div>

        {/* Risk Assessment */}
        <div style={{ 
          marginTop: '12px', 
          padding: '10px', 
          backgroundColor: '#fff3e0', 
          borderRadius: '4px',
          borderLeft: '3px solid #ffc107'
        }}>
          <p style={{ margin: '0', fontSize: '12px', color: '#7a4419' }}>
            <strong>⚠️ Why this matters:</strong> Large PRs are harder to review thoroughly, increasing bug risk. They may combine unrelated changes, making revert difficult.
          </p>
        </div>

        {/* Recommended Actions */}
        <div style={{ marginTop: '12px' }}>
          <p style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: 'bold', color: '#7a4419' }}>
            ✅ Recommended actions:
          </p>
          <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '12px', color: '#555', lineHeight: '1.8' }}>
            <li>Review comment density: How many comments per changed line?</li>
            <li>In future: Enforce max 500 lines/PR rule</li>
            <li>Split related changes into separate PRs</li>
            <li>Use feature flags for large changes</li>
          </ul>
        </div>
      </div>
    );
  };

  const renderMergeConflicts = () => {
    return (
      <div style={{ padding: '15px', backgroundColor: '#ffebee', borderRadius: '6px', marginTop: '12px' }}>
        <h5 style={{ margin: '0 0 12px 0', color: '#721c24', fontSize: '14px', fontWeight: 'bold' }}>
          ⚔️ Merge Conflicts Investigation
        </h5>

        <div style={{ 
          padding: '12px', 
          backgroundColor: '#ffffff', 
          borderRadius: '4px', 
          marginBottom: '12px',
          border: '1px solid #f5c6cb'
        }}>
          <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#333' }}>
            <strong>Issue:</strong> PR had merge conflicts that had to be resolved
          </p>
          <p style={{ margin: '0', fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
            This indicates PR was out of sync with base branch when merged
          </p>
        </div>

        <div style={{ marginTop: '12px' }}>
          <p style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: 'bold', color: '#721c24' }}>
            🔍 How to investigate:
          </p>
          <ol style={{ margin: '8px 0', paddingLeft: '20px', fontSize: '12px', color: '#555', lineHeight: '1.8' }}>
            <li>View PR history → Look for "Resolve conflicts" commits</li>
            <li>Check which files had conflicts</li>
            <li>Verify conflict resolutions were correct</li>
            <li>Check if tests passed after conflict resolution</li>
          </ol>
        </div>

        <div style={{ 
          marginTop: '12px', 
          padding: '10px', 
          backgroundColor: '#ffebee', 
          borderRadius: '4px',
          borderLeft: '3px solid #dc3545'
        }}>
          <p style={{ margin: '0', fontSize: '12px', color: '#721c24' }}>
            <strong>⚠️ Risk:</strong> Conflicting changes can introduce bugs if not carefully resolved. Manual conflict resolution increases error risk.
          </p>
        </div>
      </div>
    );
  };

  const renderReviewVelocity = () => {
    return (
      <div style={{ padding: '15px', backgroundColor: '#e8f5e9', borderRadius: '6px', marginTop: '12px' }}>
        <h5 style={{ margin: '0 0 12px 0', color: '#2e7d32', fontSize: '14px', fontWeight: 'bold' }}>
          ⚡ Review Velocity Analysis
        </h5>

        <div style={{ 
          padding: '12px', 
          backgroundColor: '#ffffff', 
          borderRadius: '4px', 
          marginBottom: '12px',
          border: '1px solid #c8e6c9'
        }}>
          <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#333' }}>
            <strong>What this means:</strong> Time between first review request and merge was unusually fast or slow
          </p>
          <p style={{ margin: '0', fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
            Very fast review (minutes) OR very slow (days) both indicate potential issues
          </p>
        </div>

        <div style={{ marginTop: '12px' }}>
          <p style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: 'bold', color: '#2e7d32' }}>
            🔍 How to investigate:
          </p>
          <ol style={{ margin: '8px 0', paddingLeft: '20px', fontSize: '12px', color: '#555', lineHeight: '1.8' }}>
            <li>View PR timeline (created → first review → approved → merged)</li>
            <li>If very fast (&lt;5 min review): Was it a rubber stamp?</li>
            <li>If very slow (&gt;5 days): Was author blocked waiting for review?</li>
            <li>Check who reviewed and their review depth</li>
          </ol>
        </div>
      </div>
    );
  };

  const renderCodeChurn = () => {
    return (
      <div style={{ padding: '15px', backgroundColor: '#f3e5f5', borderRadius: '6px', marginTop: '12px' }}>
        <h5 style={{ margin: '0 0 12px 0', color: '#6a1b9a', fontSize: '14px', fontWeight: 'bold' }}>
          🔄 Code Churn Analysis
        </h5>

        <div style={{ 
          padding: '12px', 
          backgroundColor: '#ffffff', 
          borderRadius: '4px', 
          marginBottom: '12px',
          border: '1px solid #e1bee7'
        }}>
          <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#333' }}>
            <strong>What this means:</strong> The same files or code sections are being changed repeatedly
          </p>
          <p style={{ margin: '0', fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
            Indicates instability, technical debt, or incomplete solutions
          </p>
        </div>

        <div style={{ marginTop: '12px' }}>
          <p style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: 'bold', color: '#6a1b9a' }}>
            🔍 How to investigate:
          </p>
          <ol style={{ margin: '8px 0', paddingLeft: '20px', fontSize: '12px', color: '#555', lineHeight: '1.8' }}>
            <li>Look at git blame/history for files in this PR</li>
            <li>How many times were these files changed in last 30 days?</li>
            <li>Are the same developers working on them repeatedly?</li>
            <li>Is there a pattern (e.g., bug fix → bug fix)?</li>
          </ol>
        </div>

        <div style={{ 
          marginTop: '12px', 
          padding: '10px', 
          backgroundColor: '#f3e5f5', 
          borderRadius: '4px',
          borderLeft: '3px solid #7b1fa2'
        }}>
          <p style={{ margin: '0', fontSize: '12px', color: '#6a1b9a' }}>
            <strong>📊 Insight:</strong> High churn suggests architectural or design issues. May need refactoring rather than incremental fixes.
          </p>
        </div>
      </div>
    );
  };

  // Render appropriate component based on risk type
  const renderRiskDetails = () => {
    switch (riskType) {
      case 'unresolved-comments':
        return renderUnresolvedComments();
      case 'review-comments':
        return renderReviewComments();
      case 'large-pr':
        return renderLargePR();
      case 'merge-conflicts':
        return renderMergeConflicts();
      case 'review-velocity':
        return renderReviewVelocity();
      case 'code-churn':
        return renderCodeChurn();
      default:
        return (
          <div style={{ padding: '15px', backgroundColor: '#e9ecef', borderRadius: '6px', marginTop: '12px' }}>
            <p style={{ margin: '0', fontSize: '12px', color: '#666' }}>
              Risk type not specifically covered. Click the "?" button to ask AI for more details.
            </p>
          </div>
        );
    }
  };

  return (
    <div style={{ marginTop: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px', overflow: 'hidden' }}>
      <button
        onClick={() => setExpandedSection(expandedSection === 'details' ? null : 'details')}
        style={{
          width: '100%',
          padding: '10px',
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          cursor: 'pointer',
          fontSize: '12px',
          fontWeight: '600',
          color: '#495057',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
        onMouseOver={(e) => e.target.style.backgroundColor = '#e9ecef'}
        onMouseOut={(e) => e.target.style.backgroundColor = '#f8f9fa'}
      >
        <span>📍 View Detailed Analysis</span>
        <span style={{ fontSize: '10px' }}>{expandedSection === 'details' ? '▼' : '▶'}</span>
      </button>
      
      {expandedSection === 'details' && renderRiskDetails()}
    </div>
  );
};

export default RiskDetailsViewer;
